import { Dialog } from 'primereact/dialog';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import linkIcon from 'images/icons/svg/link-1.svg';
import { DocumentVersionModel } from 'types/dynamic-forms/DocumentVersionModel';
import { TextButton } from 'components/v2/buttons';
import FontIcon from 'components/icons/FontIcon';
import { formatDate } from 'util/utils';
import { EpdLinks } from 'routes/EpdRoutes';

interface VersionItemProps {
  docId: string;
  data: DocumentVersionModel;
  onClick: React.MouseEventHandler<HTMLAnchorElement>;
}

const VersionItem: React.FunctionComponent<VersionItemProps> = ({ docId, data, onClick }) => {
  return (
    <StyledLink to={EpdLinks.dynamicFormEpdVersionLog(docId, data.id)} target="_blank" rel="noopener noreferrer" onClick={onClick}>
      <FullIdentificationNumberContainer>{data.fullIdentificationNumber}</FullIdentificationNumberContainer>
      <VersionDateAndDetailsContainer>
        {formatDate(data.versionDate)}

        <ShowDetailsIcon src={linkIcon} alt="link" />
      </VersionDateAndDetailsContainer>
    </StyledLink>
  );
};

const VersionHistoryDialog: React.FunctionComponent<{ docId: string; versions: DocumentVersionModel[] }> = ({
  docId,
  versions,
}) => {
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);

  const handleVersionClick = () => {
    // Note: Don't prevent event's default behavior and let it open the page in a new tab
    setIsVersionHistoryOpen(false);
  };

  return (
    <>
      <TextButton
        type="button"
        style={{ fontSize: 14, fontWeight: 500 }}
        text
        label="Version log"
        icon={<FontIcon icon="link" style={{ fontSize: '12px', marginRight: '5px' }} />}
        onClick={() => setIsVersionHistoryOpen(true)}
      />
      <StyledDialog
        header="Version log"
        visible={isVersionHistoryOpen}
        style={{ width: 560 }}
        onHide={() => setIsVersionHistoryOpen(false)}
      >
        <DialogContentContainer>
          <InformationContainer style={{ color: 'rgba(21, 21, 21, 0.8)' }}>
            Here you can find the version log for this EPD.
            <br />
            The selected EPD version will open in a new tab.
          </InformationContainer>
          {versions.map((version) => (
            <VersionItem key={version.id} docId={docId} data={version} onClick={handleVersionClick} />
          ))}
        </DialogContentContainer>
      </StyledDialog>
    </>
  );
};

const StyledDialog = styled(Dialog)`
  .p-dialog-header {
    padding: 30px 14px;
  }
  .p-dialog-content {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    line-height: 20px;
  }
  .p-dialog-title {
    margin: 0px !important;
    padding: 0px !important;
  }
  .p-dialog-header-close {
    position: absolute;
    right: 14px;
    top: 14px;
  }
  .p-dialog-header-close svg path {
    fill: ${(props) => props.theme.colors.primaryBlack};
  }
`;

const DialogContentContainer = styled.div`
  padding: 0 2rem 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InformationContainer = styled.div`
  background-color: ${(props) => props.theme.colors.elementsFilledInput};
  text-align: justify;
  padding: 1rem;
  border-radius: 4px;
`;

const StyledLink = styled(Link)`
  background-color: #b4d0b64d;
  color: inherit;
  text-align: justify;
  text-decoration: none;
  padding: 14px 16px 14px 16px;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FullIdentificationNumberContainer = styled.div`
  font-size: 14px;
  color: ${(props) => props.theme.colors.primaryBlack};
`;

const VersionDateAndDetailsContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: ${(props) => props.theme.colors.primaryBlack};
`;

const ShowDetailsIcon = styled.img`
  width: 15px;
  height: 15px;
  margin-left: 10px;
`;

export default VersionHistoryDialog;
